/**
 * The resolved rule shapes for a place (states/shapes.ts): the state layer, then its health authority's layer
 * (NRS 580.210(2) in Nevada; the owner's "state then county" rule). Every behavior that differs by state or authority reads
 * from here, so a new state is data (states/<st>-shapes.ts) plus research, no code (docs/STATES.md "How to add a state").
 *
 * The app's POLICY on a shape (what it does, not what the law says) lives here too, marked as such:
 *  - P1 Never block safety. A card or certificate that is due or late flags the person and reminds the manager. It never
 *    blocks a check-in, a temperature log, a cleanup or any safety step. The one hard stop kept is Utah's existing rule
 *    (Utah Code 26B-7-413: food work without the permit only within 30 days of starting), which blocks FOOD WORK on a shift
 *    after day 30, never the check-in itself (schedule.ts shiftGate). `before_first_work` (SNHD 2-601(A)) is a flag only:
 *    the coordinator's default for the owner, 2026-10-02 ("it flags the hire and reminds the manager; it never blocks").
 *  - P2 An estimate is labeled an estimate. The grade estimate is never a promise of a grade.
 *  - P3 Unknown stays unknown: an `unknown` shape shows nothing as a rule (the screens say "not mapped yet" or "ask").
 */
import type { Credential, IsoDate, PermitType } from "./model.ts";
import type { FormRow } from "./inspectionform.ts";
import { inspectorEstimate, inspectorFormAt } from "./inspector.ts";
import { departmentFor, type LocalDepartment } from "./localrules.ts";
import { contactsOf, countyIn, packFor, stateOrDefault } from "./states/index.ts";
import type { Agency, UsState } from "./states/types.ts";
import { sourcedValues, type Applicability, type CheckLawShape, type EventShape, type AuthorityShapes, type CredentialShape, type Deadline, type HomeShape, type ManagerShape, type MobileShape, type PostingShape, type RuleShapes, type SanitizerShape, type ScoringShape, type Sourced, type StateShapes } from "./states/shapes.ts";
import { UT_SHAPES } from "./states/ut-shapes.ts";
import { perStateFrom } from "./states/access.ts";
import { appliedRulesIn, defaultWorld, keysIn, resolveIn, RULE_KEY, type Place, type Resolution, type World } from "./jurisdiction.ts";

export * from "./states/shapes.ts";

/** Every state's rule shapes: Utah's here, another state's from its bundle once loaded (states/loaders.ts). */
export const SHAPES: Record<UsState, StateShapes> = perStateFrom<StateShapes, Record<string, StateShapes>>("shapes", (m, st) => m[`${st}_SHAPES`]!, UT_SHAPES);

// ── Health authority graph: state → authority → counties (and cities) ────────────────────────────────────────────────

export type AuthorityRef =
  /** The county's own health authority, confirmed from its pages. */
  | { kind: "confirmed"; state: UsState; department: LocalDepartment; phone?: string; office?: string }
  /** The county is the state's, but who inspects food there isn't confirmed: name the state food program and its number. */
  | { kind: "unconfirmed"; state: UsState; county: string; stateProgram: Agency; phone?: string }
  /** No county given yet ("Not sure yet"): state rules only. */
  | { kind: "no-county"; state: UsState };

const norm = (c: string) => c.trim().toLowerCase().replace(/\s+(county|co\.?)$/, "").trim();

/** Who inspects food at a place in this county. Never guessed: an unclaimed county is `unconfirmed`. */
export function authorityFor(state: UsState, county?: string | null): AuthorityRef {
  const c = countyIn(state, county);
  if (!c) return { kind: "no-county", state };
  const department = departmentFor(state, c);
  if (department) {
    const mine = contactsOf(state).filter((x) => x.department === department.id);
    const hit = mine.find((x) => x.counties?.some((y) => norm(y) === norm(c))) ?? mine.find((x) => !x.counties);
    return { kind: "confirmed", state, department, ...(hit ? { phone: hit.phone, ...(hit.office ? { office: hit.office } : {}) } : {}) };
  }
  const prog = packFor(state).stateAgency;
  return { kind: "unconfirmed", state, county: c, stateProgram: prog, ...(prog.phones[0] ? { phone: prog.phones[0].number } : {}) };
}

/** One plain line naming who to ask, honest about an unconfirmed county. */
export function authorityLine(a: AuthorityRef): string {
  if (a.kind === "confirmed") return `${a.department.name}${a.phone ? `, ${a.phone}` : ""}`;
  if (a.kind === "unconfirmed") return `Who inspects food in ${a.county} County isn't confirmed yet. Ask the state food program: ${a.stateProgram.name}${a.phone ? `, ${a.phone}` : ""}.`;
  return `Pick your county to see who inspects you. Until then, these are ${packFor(a.state).name}'s state rules.`;
}

// ── Resolution ───────────────────────────────────────────────────────────────────────────────────────────────────────

export type Layer = "state" | "authority";
export interface ResolvedShapes {
  state: UsState;
  authority: AuthorityRef;
  shapes: RuleShapes;
  /** Which layer each top-level shape (and each mobile/home/events field) came from. */
  from: Record<string, Layer>;
}

/** The shapes in force at a place: the state layer, then its confirmed authority's. An unconfirmed county gets the state layer. */
export function shapesAt(place: { state?: string | null; county?: string | null }): ResolvedShapes {
  const st: UsState = stateOrDefault(place.state);
  const authority = authorityFor(st, place.county);
  // Every shape is read through the jurisdiction resolver (jurisdiction.ts): country, state, then the place's authority,
  // each by its relation. The shapes files are the authoring format; this is the one runtime path.
  const world = rulesWorld();
  const at = { state: st, county: authority.kind === "confirmed" ? (countyIn(st, place.county) ?? "") : "" };
  const from: Record<string, Layer> = {};
  const one = <T>(key: string, fromKey: string): Sourced<T> => {
    const r = resolveIn<T>(world, key, at);
    from[fromKey] = r.rule && r.rule.node !== st && r.rule.node !== "US" ? "authority" : "state";
    return asSourced<T>(r);
  };
  const many = <T>(prefix: string): [string, Sourced<T>][] => keysIn(world, at, prefix).map((k) => [k.slice(prefix.length), asSourced<T>(resolveIn<T>(world, k, at))]);
  const group = <T extends object>(k: "mobile" | "home" | "events", fields: readonly string[]): T =>
    Object.fromEntries(fields.map((f) => [f, one(RULE_KEY[`${k}.${f}`]!, `${k}.${f}`)])) as T;
  const shapes: RuleShapes = {
    foodHandler: one("food_handler.rule", "foodHandler"),
    // A manager rule is a legal duty ("Here, the person in charge must hold one"). The FDA model's 2-102.12 is guidance, not law,
    // where no state or county rule was found (New York outside NYC and the counties that add one: 10 NYCRR 14-1.70 to 14-1.73
    // has no certificate rule), so a guidance-only answer is "not known here", never shown or flagged as a requirement.
    manager: lawOnly(one("manager.rule", "manager"), resolveIn<ManagerShape>(world, "manager.rule", at)),
    permits: Object.fromEntries(many<Deadline>("permit_renewal.")) as RuleShapes["permits"],
    scoring: one("inspection.scoring", "scoring"),
    // A district adds signs; it never takes away the state's (keysIn: the state's first, then the district's).
    postings: keysIn(world, at, "posting.").map((k) => resolveIn<PostingShape>(world, k, at).value!).filter(Boolean),
    mobile: group<MobileShape>("mobile", ["waterTankMinGallons", "coolingOnBoard", "hoursPerSpot", "routeLog"]),
    home: group<HomeShape>("home", ["model", "privateHomeFoodBusinessBarred"]),
    edition: Object.fromEntries(many<Applicability>("edition.")),
    events: group<EventShape>("events", ["applyBusinessDaysAhead", "maxConsecutiveDays", "boothMaxSqFt"]),
    illness: one("illness.report", "illness"),
    coldHold: one("cold_holding.max_temp", "coldHold"),
  };
  // A state's own manual-sanitizing minimums, only where it has them (California): an optional shape, so a state without one
  // shows nothing here rather than "unknown" (the FDA model's range is the template's own).
  const sanitizer = one<SanitizerShape>("sanitizer.manual_min", "sanitizer");
  if (sanitizer.status === "verified") shapes.sanitizer = sanitizer; else delete from.sanitizer;
  return { state: st, authority, shapes, from };
}

/** A guidance-only resolution (the FDA model, no law found) as unknown, saying what was found; any law value passes through. */
function lawOnly<T>(s: Sourced<T>, r: Resolution<T>): Sourced<T> {
  if (r.status !== "guidance") return s;
  return { status: "unknown", tried: `${r.tried?.text ?? "No state or local rule found"}. The FDA Food Code (a model, not law here) recommends it${r.rule?.source?.section ? ` (${r.rule.source.section})` : ""}.`, lastChecked: r.tried?.lastChecked ?? r.rule?.lastChecked ?? "2026-10-02" };
}

/** A resolution as the shape's Sourced value: law, adopted, amended or guidance = verified (its source); unknown = unknown. */
function asSourced<T>(r: Resolution<T>): Sourced<T> {
  if (r.rule && r.status !== "unknown") return { status: "verified", value: r.value as T, source: r.rule.source!, ...(r.rule.note ? { note: r.rule.note } : {}) };
  return { status: "unknown", tried: r.tried?.text ?? "not researched", lastChecked: r.tried?.lastChecked ?? "2026-10-02" };
}

/** The default world (US, Utah, Nevada and their authorities, every rule value). */
export const rulesWorld = (): World => defaultWorld(SHAPES);

/** The rules a new record of this log is judged by, at this place, today (LogEntry.evaluation.appliedRules). */
export const appliedRulesFor = (templateId: string, place: Place, date: IsoDate) => appliedRulesIn(rulesWorld(), templateId, place, date);

/** Resolve one rule key for a place (the jurisdiction resolver on the default world). */
export const resolveRule = <T = unknown>(key: string, place: Place, o: { date?: IsoDate; scope?: { permit?: PermitType; risk?: string } } = {}): Resolution<T> => resolveIn<T>(rulesWorld(), key, place, o);

/** The value of a verified shape, else undefined (P3). */
export const valueOf = <T>(s: Sourced<T> | undefined): T | undefined => (s?.status === "verified" ? s.value : undefined);

// ── What the law requires of each built-in check ─────────────────────────────────────────────────────────────────────

/**
 * One built-in check's standing in the law at a place (docs/compliance/CHECKS-BY-LAW.md):
 *  - `law`: the law names this record (or daily act) for this permit, at its own cadence (`value`), with a plain line;
 *  - `none`: read, and not required here (a verified null, e.g. Oregon's trucks need no commissary);
 *  - `unknown`: not read far enough to say; `confirm` names who to ask. The check stays the app's advice (P3).
 */
export type CheckLaw =
  | { status: "law"; value: CheckLawShape; citation: string; url: string }
  | { status: "none"; citation: string }
  | { status: "unknown"; tried: string; confirm?: { name: string; phone?: string } };

/**
 * Every built-in check the law at this place says something about, for this permit type: rule key → CheckLaw. A check with no
 * entry is the app's advice. Read through the jurisdiction resolver (state, then the place's confirmed authority), so a county
 * rule (Utah County's daily commissary log, New York City's daily pest look) lands on top of its state's.
 */
export function checkLawsAt(place: { state?: string | null; county?: string | null }, permit: PermitType): Record<string, CheckLaw> {
  const st: UsState = stateOrDefault(place.state);
  const authority = authorityFor(st, place.county);
  const world = rulesWorld();
  const at = { state: st, county: authority.kind === "confirmed" ? (countyIn(st, place.county) ?? "") : "" };
  const out: Record<string, CheckLaw> = {};
  for (const key of keysIn(world, at, "check.")) {
    const r = resolveIn<CheckLawShape | null>(world, key, at);
    const ck = key.slice("check.".length);
    if (r.rule && r.status !== "unknown" && r.status !== "guidance") {
      if (r.value === null) out[ck] = { status: "none", citation: r.rule.source?.citation ?? "" };
      else if (r.value && r.value.permits.includes(permit)) out[ck] = { status: "law", value: r.value, citation: r.rule.source?.citation ?? "", url: r.rule.source?.url ?? "" };
      // A law for another permit type says nothing about this one: no entry (the check stays advice here).
    } else if (r.status === "unknown") {
      // "Ask them" names a department, never a bare state: the state node's name is the state's, so its food program stands in.
      const confirm = r.confirmWith && r.confirmWith.name === packFor(st).name ? { ...r.confirmWith, name: packFor(st).stateAgency.name } : r.confirmWith;
      out[ck] = { status: "unknown", tried: r.tried?.text ?? "not researched", ...(confirm ? { confirm } : {}) };
    }
  }
  return out;
}

// ── Deadline: the food handler card clock ────────────────────────────────────────────────────────────────────────────

/** The owner's policy per deadline kind (P1): does a late step stop FOOD WORK on a shift? Never the check-in or safety work. */
export const DEADLINE_POLICY: Record<Deadline["kind"], { blocksFoodWorkWhenLate: boolean; why: string }> = {
  within_days: { blocksFoodWorkWhenLate: true, why: "The state rule (26B-7-413) allows food work without the permit only inside its window; the app kept this stop before the shapes." },
  before_first_work: { blocksFoodWorkWhenLate: false, why: "Owner default (2026-10-02): flag the hire and remind the manager; never block safety work or the check-in." },
  renew_every_years: { blocksFoodWorkWhenLate: false, why: "An expiring card is a reminder." },
  before: { blocksFoodWorkWhenLate: false, why: "A lead time for an application (opening, an event, building): guidance, never a block." },
};

const days = (from: IsoDate, to: IsoDate) => Math.round((Date.parse(to + "T00:00:00Z") - Date.parse(from + "T00:00:00Z")) / 86400000);

export interface CardClock {
  /** A food handler card is required here. False = no card rule at this place (Nevada outside Clark County). */
  required: boolean;
  /** Each step with its due day (0 = due before the first shift), in days left from today (negative = late). */
  steps: { id: "course" | "card"; label: string; due: Deadline; dueInDays: number | null }[];
  /** True when the card itself is due before the first shift (SNHD). */
  beforeFirstWork: boolean;
  /** Policy P1: whether a late card stops food work on a shift. */
  blocksFoodWorkWhenLate: boolean;
  /** Short status line for the People screen ("Card needed before first shift"), or null when nothing is due. */
  line: string | null;
  /** The rule's citation ("SNHD 2023 Food Regulations 2-601(A)"); "" when no rule. */
  citation: string;
}

/**
 * The food handler card clock for one person. `from` = the first day of food work (journey.ts clockStart), or null when
 * the place isn't serving food yet. `hasCard` = a valid card or manager certificate is on file.
 */
export function cardClock(shape: Sourced<CredentialShape>, o: { from: IsoDate | null; today: IsoDate; hasCard: boolean }): CardClock {
  const s = valueOf(shape);
  const citation = shape.status === "verified" ? shape.source.citation.split(";")[0]!.trim() : "";
  if (!s || !s.required) return { required: false, steps: [], beforeFirstWork: false, blocksFoodWorkWhenLate: false, line: null, citation };
  const worked = o.from ? days(o.from, o.today) : 0;
  const steps = s.steps.map((x) => ({ ...x, dueInDays: x.due.kind === "within_days" ? x.due.days - worked : x.due.kind === "before_first_work" ? -worked : null }));
  const card = s.steps.find((x) => x.id === "card");
  const beforeFirstWork = card?.due.kind === "before_first_work";
  const blocks = card ? DEADLINE_POLICY[card.due.kind].blocksFoodWorkWhenLate : false;
  const line = o.hasCard ? null : beforeFirstWork ? "Card needed before first shift" : null;
  return { required: true, steps, beforeFirstWork, blocksFoodWorkWhenLate: blocks, line, citation };
}

// ── Manager: the person in charge's certificate ──────────────────────────────────────────────────────────────────────

export const hasValidCert = (creds: readonly Pick<Credential, "kind" | "expiresOn">[], today: IsoDate) =>
  creds.some((c) => c.kind === "certified_food_safety_manager" && (!c.expiresOn || c.expiresOn >= today));

export interface PicCertNote { title: string; detail: string; citation: string; action: string }
/** The note's title (sickprivacy.ts findingsForViewer keeps it from coworkers: it is for managers). */
export const PIC_CERT_TITLE = "Needs a certified manager";

/**
 * A gentle note for managers when the person in charge isn't a certified food protection manager where the authority
 * requires it (SNHD 2-102.2: "The PIC must be a certified food protection manager"). Never a block (P1). Not for a
 * temporary booth (2-102.2(B) exempts temporary places). Null when the rule doesn't apply or the PIC holds a certificate.
 */
export function picCertNote(r: ResolvedShapes, o: { picName?: string; picCreds: readonly Pick<Credential, "kind" | "expiresOn">[]; today: IsoDate; permit?: PermitType }): PicCertNote | null {
  const m = valueOf(r.shapes.manager);
  if (!m || m.rule !== "pic_must_be_certified" || !o.picName) return null;
  if (o.permit === "temporary_event") return null;
  if (hasValidCert(o.picCreds, o.today)) return null;
  const src = r.shapes.manager.status === "verified" ? r.shapes.manager.source.citation : "";
  return {
    title: PIC_CERT_TITLE,
    detail: `${o.picName} is in charge but has no food protection manager certificate on file. Here, the person in charge must hold one.`,
    citation: src,
    action: "Add the certificate",
  };
}

/**
 * Whether this person should get a food protection manager certificate here (the person page's "Approved food protection manager
 * exams" and the "Add proof" slot; fix/permit-temp-display, owner 2026-10-10: the heading showed for someone who needs none).
 * "pic_must_be_certified" (SNHD 2-102.2): the person in charge without one (picCertNote). "one_certified_on_staff" (Utah Code
 * 26B-7-412): the person in charge, while nobody on the team holds a current one. Never for a temporary booth; unknown rule: no.
 */
export function needsManagerCertificate(r: ResolvedShapes, o: { isPersonInCharge: boolean; name: string; ownCreds: readonly Pick<Credential, "kind" | "expiresOn">[]; teamCreds: readonly Pick<Credential, "kind" | "expiresOn">[]; today: IsoDate; permit?: PermitType }): boolean {
  if (!o.isPersonInCharge || o.permit === "temporary_event") return false;
  const m = valueOf(r.shapes.manager);
  if (m?.rule === "pic_must_be_certified") return !!picCertNote(r, { picName: o.name, picCreds: o.ownCreds, today: o.today, permit: o.permit });
  if (m?.rule === "one_certified_on_staff") return !hasValidCert(o.teamCreds, o.today);
  return false;
}

/** The manager rule in one plain line, or null when unknown. */
export function managerLine(m: Sourced<ManagerShape>): string | null {
  const x = valueOf(m);
  if (!x) return null;
  const renew = x.renew ? ` Renew every ${x.renew.years} years.` : "";
  const due = x.due ? ` A new place has ${x.due.days} days to get one.` : "";
  const levels = x.riskLevels ? ` (Risk Level ${x.riskLevels.join(" and ")})` : "";
  if (x.effective) return `${managerRuleLine(x, renew, due, levels)} Required from ${x.effective}; until then we recommend it.`;
  return managerRuleLine(x, renew, due, levels);
}
function managerRuleLine(x: ManagerShape, renew: string, due: string, levels: string): string {
  switch (x.rule) {
    case "pic_must_be_certified": return `The person in charge on every shift holds a food protection manager certificate.${renew}`;
    case "one_certified_on_staff": return `At least one certified food protection manager on staff${levels}.${due}${renew}`;
    case "knowledge_shown": return `The person in charge shows the inspector they know food safety. A manager certificate is one way.`;
  }
}

// ── Scoring: the grade readiness estimate ────────────────────────────────────────────────────────────────────────────

export const ESTIMATE_LABEL = "Estimate only. Not a grade.";

export type GradeEstimate =
  | { kind: "demerits"; label: string; demerits: number; band: string; closed: boolean; counted: { n: number; title: string; weight: number }[]; notSeen: number; total: number; line: string; citation: string }
  | { kind: "pass_conditional_fail"; label: string; tiers: { status: string; text: string }[]; openProblems: number; line: string; citation: string }
  | { kind: "points"; label: string; values: number[]; openProblems: number; line: string; citation: string }
  /** The form's own published deductions where a finding's class is not known in advance (Boston 2 to 10, LA 2 to 4): a range, labeled. */
  | { kind: "range"; label: string; low: number; high: number; startAt: number; bandLow: string; bandHigh: string; counted: { id: string; text: string; min: number; max: number }[]; notSeen: number; total: number; line: string; note?: string; citation: string }
  /** Classed findings, no grade (Florida DBPR: High Priority, Intermediate, Basic; "not graded or rated"). */
  | { kind: "classes"; label: string; byClass: { id: string; label: string; items: string[] }[]; openItems: number; line: string; citation: string }
  | { kind: "not_mapped"; line: string };

/**
 * "If inspected now": each item on the authority's own form that has an open problem on file (inspectionform.ts rows with
 * status "problem") counts its weight once. Items we have no records for are counted as unseen, never as clean (P2, P3).
 */
export function gradeEstimate(r: ResolvedShapes, rows: readonly (Pick<FormRow, "n" | "status"> & { failing?: boolean; itemIds?: string[] })[]): GradeEstimate {
  const sc = r.shapes.scoring;
  const s: ScoringShape | undefined = valueOf(sc);
  const citation = sc.status === "verified" ? sc.source.citation : "";
  const problems = rows.filter((x) => x.status === "problem").length;
  // The place's own inspector form, when it publishes how it scores (inspector.ts): Boston and LA County as a range, Florida's
  // classes (DBPR: "not graded or rated"). Only where the rule shapes have no form of their own (SNHD's stays as it is).
  const own = inspectorFormAt(r.state, r.authority.kind === "confirmed" ? r.authority.department.id : undefined);
  const mine = own ? inspectorEstimate(own, rows) : null;
  if (own && mine?.kind === "classes" && (!s || s.kind === "none")) {
    const top = mine.byClass[0];
    const line = mine.openItems
      ? `${own.issuerShort ?? own.issuer} does not grade or rate establishments. ${mine.openItems} item${mine.openItems === 1 ? "" : "s"} on its form ${mine.openItems === 1 ? "has" : "have"} an open problem on file${top && top.items.length ? `; ${top.items.length} can be cited ${top.label} (${top.items.join(", ")})` : ""}.`
      : `${own.issuerShort ?? own.issuer} does not grade or rate establishments. No open problem on file sits on its form's items.`;
    return { kind: "classes", label: "Not a grade.", byClass: mine.byClass, openItems: mine.openItems, line, citation: mine.citation };
  }
  if (mine?.kind === "range" && s?.kind === "score_bands" && !s.form.length) {
    const bandOf = (score: number) => s.bands.find((b) => score >= b.min && score <= b.max)?.grade ?? "?";
    const bandLow = bandOf(mine.low), bandHigh = bandOf(mine.high);
    const likely = bandLow === bandHigh ? bandHigh : `${bandHigh} to ${bandLow}`;
    const line = mine.low === mine.high
      ? `About ${mine.high} of ${mine.startAt} from open problems on file: likely ${likely} if inspected now.`
      : `About ${mine.low} to ${mine.high} of ${mine.startAt} from open problems on file: likely ${likely} if inspected now.`;
    return { kind: "range", label: ESTIMATE_LABEL, low: mine.low, high: mine.high, startAt: mine.startAt, bandLow, bandHigh, counted: mine.counted, notSeen: mine.notSeen, total: mine.total, line, ...(mine.note ? { note: mine.note } : {}), citation: mine.citation };
  }
  if (!s || s.kind === "none") return { kind: "not_mapped", line: "Your health department's scoring is not mapped yet. The form below shows what's on file." };
  // A scale whose form isn't mapped (California counties: bands from the county's page, no form read) never guesses a grade:
  // an empty form would read as a perfect score. It names the scale and says the estimate isn't available.
  if ((s.kind === "score_bands" || s.kind === "demerit_bands") && !s.form.length) {
    const scale = s.bands.map((b) => (b.max >= 999 ? `${b.grade} ${b.min} or more` : `${b.grade} ${b.min}–${b.max}`)).join(", ");
    return { kind: "not_mapped", line: `Your health department grades on this scale: ${scale}${s.kind === "score_bands" ? ` (out of ${s.startAt}, higher is better)` : " (points, lower is better)"}. Its inspection form isn't mapped yet, so there's no estimate.` };
  }
  if (s.kind === "points_accumulated" && !s.values.length) return { kind: "not_mapped", line: `Your health department scores inspections by points; how many each item takes off isn't mapped yet. ${problems} open problem${problems === 1 ? "" : "s"} on file.` };
  if (s.kind === "points_accumulated") return { kind: "points", label: ESTIMATE_LABEL, values: s.values, openProblems: problems, line: `Each violation adds ${s.values.join(", ").replace(/, (\d+)$/, " or $1")} points; lower is better. ${problems} open problem${problems === 1 ? "" : "s"} on file. Which items weigh what is not mapped yet.`, citation };
  if (s.kind === "pass_conditional_fail") return { kind: "pass_conditional_fail", label: ESTIMATE_LABEL, tiers: s.tiers, openProblems: problems, line: `Results are Pass, Conditional Pass, Failed or Closed, by the number of critical violations. ${problems} open problem${problems === 1 ? "" : "s"} on file.`, citation };
  // Counted: a problem that actually failed (a late check alone isn't a violation an inspector would cite); a 0-weight item adds nothing.
  const byN = new Map(rows.map((x) => [x.n, x.status === "problem" && x.failing === false ? "late" : x.status]));
  // One problem is cited once: each failing record (or, without ids, each FDA row) counts toward the heaviest authority item it
  // maps to, never toward two (a warm case is item 9, not also 10 and 19).
  const idsOf = new Map(rows.map((x) => [x.n, x.itemIds?.length ? x.itemIds : [`row:${x.n}`]]));
  const claimed = new Set<string>();
  const counted: { n: number; title: string; weight: number }[] = [];
  for (const it of [...s.form].sort((a, b) => s.weights[b.severity] - s.weights[a.severity] || a.n - b.n)) {
    if (!(s.weights[it.severity] > 0)) continue;
    const ids = it.fda.filter((n) => byN.get(n) === "problem").flatMap((n) => idsOf.get(n) ?? []);
    const fresh = ids.filter((id) => !claimed.has(id));
    if (!fresh.length) continue;
    for (const id of ids) claimed.add(id);
    counted.push({ n: it.n, title: it.title, weight: s.weights[it.severity] });
  }
  counted.sort((a, b) => a.n - b.n);
  const notSeen = s.form.filter((it) => !it.fda.length || it.fda.every((n) => { const st = byN.get(n); return st === undefined || st === "not_tracked" || st === "nothing_yet"; })).length;
  const demerits = counted.reduce((a, b) => a + b.weight, 0);
  if (s.kind === "score_bands") {
    // Higher is better: the score starts at `startAt` and each counted item takes its weight off.
    const score = s.startAt - demerits;
    const band = s.bands.find((b) => score >= b.min && score <= b.max)?.grade ?? "?";
    return { kind: "demerits", label: ESTIMATE_LABEL, demerits, band, closed: false, counted, notSeen, total: s.form.length, line: `About ${score} of ${s.startAt} from open problems on file: likely ${band} if inspected now.`, citation };
  }
  const closed = s.closedAbove !== undefined && demerits > s.closedAbove;
  const band = closed ? "Closed" : s.bands.find((b) => demerits >= b.min && demerits <= b.max)?.grade ?? "?";
  const line = closed ? `About ${demerits} demerits from open problems: over ${s.closedAbove} closes the place.` : `About ${demerits} demerits from open problems on file: likely ${band} if inspected now.`;
  return { kind: "demerits", label: ESTIMATE_LABEL, demerits, band, closed, counted, notSeen, total: s.form.length, line, citation };
}

// ── Posting ──────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface PostingFacts { alcoholServed?: boolean; permit?: PermitType; /** Full- and part-time people on the roster today; absent = not known. */ employees?: number }

/** The signs this place posts. `gradeIssued`: only where the authority's scoring issues a letter grade (demerit bands). */
export function postingsFor(r: ResolvedShapes, f: PostingFacts): (PostingShape & { conditional?: string })[] {
  const grades = valueOf(r.shapes.scoring)?.kind === "demerit_bands" || valueOf(r.shapes.scoring)?.kind === "score_bands";
  return r.shapes.postings.filter((p) => {
    if (f.permit && p.notFor?.includes(f.permit)) return false;
    if (p.minEmployees && f.employees !== undefined && f.employees < p.minEmployees) return false;
    if (p.when === "alcoholServed") return !!f.alcoholServed;
    if (p.when === "gradeIssued") return grades;
    return true;
  }).map((p) => (p.minEmployees && f.employees === undefined ? { ...p, conditional: `applies if you have ${p.minEmployees}+ employees` } : p));
}

// ── Mobile limits ────────────────────────────────────────────────────────────────────────────────────────────────────

export interface TruckRule { id: "tank" | "cooling" | "hours" | "route"; title: string; line: string; citation: string }

/** The truck rules in force, in plain words; an unknown limit is left out (P3). */
export function truckRules(r: ResolvedShapes): TruckRule[] {
  const m: MobileShape = r.shapes.mobile;
  const out: TruckRule[] = [];
  const cite = (s: Sourced<unknown>) => (s.status === "verified" ? s.source.citation : "");
  const tank = valueOf(m.waterTankMinGallons);
  if (tank !== undefined) out.push({ id: "tank", title: "Fresh-water tank", line: `Your fresh-water tank holds at least ${tank} gallons.`, citation: cite(m.waterTankMinGallons) });
  const cool = valueOf(m.coolingOnBoard);
  if (cool === false) out.push({ id: "cooling", title: "No cooling on the truck", line: "Cool hot food at the commissary, never on the truck.", citation: cite(m.coolingOnBoard) });
  const h = valueOf(m.hoursPerSpot);
  if (h && (h.withRestroom !== null || h.withoutRestroom !== null)) out.push({ id: "hours", title: "Hours at one spot", line: `At most ${h.withRestroom} hours at one spot${h.withoutRestroom !== null ? `, or ${h.withoutRestroom} hour${h.withoutRestroom === 1 ? "" : "s"} where your staff have no restroom` : ""}.`, citation: cite(m.hoursPerSpot) });
  const route = valueOf(m.routeLog);
  if (route) out.push({ id: "route", title: "Monthly route sheet", line: `Send your route sheet to the health district every month. Keep it ${route.keepDays} days.`, citation: cite(m.routeLog) });
  return out;
}

/** Hours-at-a-spot check: how long the truck has been at this spot, and whether the limit is near or passed. */
export function spotStatus(r: ResolvedShapes, o: { arrivedAt: string; now: string; restroom: boolean }): { limitHours: number | null; minutesLeft: number | null; over: boolean; line: string } {
  const h = valueOf(r.shapes.mobile.hoursPerSpot);
  const limit = h ? (o.restroom ? h.withRestroom : h.withoutRestroom) : null;
  const mins = Math.round((Date.parse(o.now) - Date.parse(o.arrivedAt)) / 60000);
  if (limit === null || limit === undefined) return { limitHours: null, minutesLeft: null, over: false, line: "No hours limit at one spot in your rules." };
  const left = limit * 60 - mins;
  const fmt = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);
  return { limitHours: limit, minutesLeft: left, over: left < 0, line: left < 0 ? `Time to move: over the ${limit}-hour limit by ${fmt(-left)}.` : `${fmt(left)} left at this spot (${limit}-hour limit${o.restroom ? "" : ", no restroom"}).` };
}

/** Is the tank big enough? Null when the minimum is unknown. */
export function tankCheck(r: ResolvedShapes, gallons: number): { ok: boolean; min: number; line: string } | null {
  const min = valueOf(r.shapes.mobile.waterTankMinGallons);
  if (min === undefined) return null;
  return gallons >= min ? { ok: true, min, line: `${gallons} gallons: meets the ${min}-gallon minimum.` } : { ok: false, min, line: `${gallons} gallons is under the ${min}-gallon minimum. Ask the health district before you open.` };
}

// ── Events ───────────────────────────────────────────────────────────────────────────────────────────────────────────

/** Event setup guidance lines (apply ahead, longest permit, booth size), each only when sourced. */
export function eventGuidance(r: ResolvedShapes): { id: string; line: string; citation: string }[] {
  const e = r.shapes.events;
  const out: { id: string; line: string; citation: string }[] = [];
  const cite = (s: Sourced<unknown>) => (s.status === "verified" ? s.source.citation : "");
  const ahead = valueOf(e.applyBusinessDaysAhead);
  if (ahead !== undefined) out.push({ id: "apply", line: `Apply at least ${ahead} business day${ahead === 1 ? "" : "s"} before the event, or the permit can be denied.`, citation: cite(e.applyBusinessDaysAhead) });
  const max = valueOf(e.maxConsecutiveDays);
  if (max !== undefined) out.push({ id: "days", line: `A temporary permit lasts at most ${max} days in a row.`, citation: cite(e.maxConsecutiveDays) });
  const booth = valueOf(e.boothMaxSqFt);
  if (booth) out.push({ id: "booth", line: `Booth size: up to ${booth.higherRisk} square feet with food that needs temperature control, ${booth.lowRisk} with low-risk food only.`, citation: cite(e.boothMaxSqFt) });
  return out;
}

/** Business days before an event date (Mon–Fri; holidays not counted), for the "apply ahead" check. */
export function businessDaysBefore(today: IsoDate, eventOn: IsoDate): number {
  let n = 0;
  for (let t = Date.parse(today + "T00:00:00Z") + 86400000; t < Date.parse(eventOn + "T00:00:00Z"); t += 86400000) { const d = new Date(t).getUTCDay(); if (d !== 0 && d !== 6) n++; }
  return n;
}

// ── Home ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface HomeOptions { mehk: boolean; cottage: boolean; homeFoodBusinessBarred: boolean; lines: { id: string; line: string; citation: string }[] }

export function homeOptions(r: ResolvedShapes): HomeOptions {
  const h: HomeShape = r.shapes.home;
  const model = valueOf(h.model);
  const barred = valueOf(h.privateHomeFoodBusinessBarred) === true;
  const cite = (s: Sourced<unknown>) => (s.status === "verified" ? s.source.citation : "");
  const lines: HomeOptions["lines"] = [];
  if (model === "cottage_registration") lines.push({ id: "cottage", line: "Cottage food: register before you sell shelf-stable foods (like candy, jam, dried fruit, granola and baked goods with no cream, custard or cream cheese frosting), sold in person.", citation: cite(h.model) });
  if (barred) lines.push({ id: "barred", line: "A food business (other than cottage food) can't run from a private home here. Use a permitted kitchen.", citation: cite(h.privateHomeFoodBusinessBarred) });
  return { mehk: model === "mehk", cottage: model === "cottage_registration", homeFoodBusinessBarred: barred, lines };
}

// ── Edition, illness, cold holding ───────────────────────────────────────────────────────────────────────────────────

/** Does a Food Code requirement apply here under this edition? Absent = applies (the catalog's own rule). */
export function editionStatus(r: ResolvedShapes, requirementId: string): "applies" | "recommended" | "not_applicable" {
  return valueOf(r.shapes.edition[requirementId]) ?? "applies";
}

/** Who the person in charge tells about a sick worker, and how fast. Clark: SNHD's rule names no hours; the state's 24 is the outer limit. */
export function illnessLine(r: ResolvedShapes): { line: string; withinHours: number | null; citation: string } {
  const own = valueOf(r.shapes.illness);
  const stateShape = SHAPES[r.state].state.illness;
  const st = valueOf(stateShape);
  const cite = r.shapes.illness.status === "verified" ? r.shapes.illness.source.citation : "";
  const reach = r.authority.kind === "confirmed" ? r.authority : undefined;
  const who = reach ? `${reach.department.short}${reach.phone ? ` (${reach.phone})` : ""}` : own?.tell ?? "the health department";
  if (own?.withinHours) return { line: `Within ${own.withinHours} hours, tell ${who} about a worker with jaundice or a diagnosed illness that spreads through food.`, withinHours: own.withinHours, citation: cite };
  if (r.from.illness === "authority" && st?.withinHours) {
    const sc = stateShape.status === "verified" ? stateShape.source.citation : "";
    return { line: `Tell ${who} right away about a worker with jaundice or a diagnosed illness that spreads through food. Don't wait past ${st.withinHours} hours (${packFor(r.state).name}'s state rule).`, withinHours: st.withinHours, citation: `${cite}; ${sc}` };
  }
  return { line: `Tell ${who} about a worker with a diagnosed illness that spreads through food.`, withinHours: null, citation: cite };
}

/** Cold holding, °F or below. 41 everywhere today; an unknown falls back to the Food Code's 41 (3-501.16(A)(2)). */
export const coldHoldMaxF = (r: ResolvedShapes): number => valueOf(r.shapes.coldHold)?.maxF ?? 41;

// ── The truck card: the truck rules in force, each with what's on file ───────────────────────────────────────────────

export interface TruckCardItem extends TruckRule {
  /** ok = on file and within the rule; todo = nothing on file yet; over = on file and outside the rule; info = guidance only. */
  status: "ok" | "todo" | "over" | "info";
  /** What's on file, in one line ("40 gallons: meets the 40-gallon minimum."). */
  onFile?: string;
  /** The log to fill (templates.ts: truck_tank, truck_spot, route_sheet). */
  templateId?: string;
  action?: string;
}

type EntryLike = { templateId: string; date: IsoDate; values: Record<string, unknown>; createdAt?: string };

/**
 * The truck checks for a place (SNHD: the 40-gallon tank, no cooling on board, hours at one spot with a restroom toggle, and
 * the monthly route sheet). Only the rules whose shapes are verified for the place's authority (P3). Never a block (P1).
 */
export function truckCard(r: ResolvedShapes, o: { entries: readonly EntryLike[]; today: IsoDate; now: string; toIso: (d: IsoDate, hhmm: string) => string }): TruckCardItem[] {
  const latest = (id: string, f: (e: EntryLike) => boolean = () => true) => o.entries.filter((e) => e.templateId === id && f(e)).sort((a, b) => (b.date + (b.createdAt ?? "")).localeCompare(a.date + (a.createdAt ?? "")))[0];
  return truckRules(r).map((rule): TruckCardItem => {
    if (rule.id === "tank") {
      const e = latest("truck_tank");
      const g = Number(e?.values.gallons);
      const t = e && Number.isFinite(g) ? tankCheck(r, g) : null;
      return t ? { ...rule, status: t.ok ? "ok" : "over", onFile: t.line, templateId: "truck_tank", action: "Update the tank size" } : { ...rule, status: "todo", templateId: "truck_tank", action: "Enter your tank size" };
    }
    if (rule.id === "hours") {
      const e = latest("truck_spot", (x) => x.date === o.today && !x.values.left);
      if (!e || typeof e.values.arrived !== "string") return { ...rule, status: "todo", templateId: "truck_spot", action: "Start the clock at this spot" };
      const s = spotStatus(r, { arrivedAt: o.toIso(o.today, e.values.arrived), now: o.now, restroom: e.values.restroom === "yes" });
      return { ...rule, status: s.over ? "over" : "ok", onFile: `${String(e.values.spot ?? "This spot")}: ${s.line}`, templateId: "truck_spot", action: "Log leaving this spot" };
    }
    if (rule.id === "route") {
      const month = o.today.slice(0, 7);
      const e = latest("route_sheet", (x) => x.date.slice(0, 7) === month);
      return e?.values.sent === "yes" ? { ...rule, status: "ok", onFile: "This month's route sheet is sent.", templateId: "route_sheet", action: "Update the route sheet" }
        : { ...rule, status: "todo", ...(e ? { onFile: "This month's route sheet isn't sent yet." } : {}), templateId: "route_sheet", action: "Fill this month's route sheet" };
    }
    return { ...rule, status: "info" };
  });
}

// ── The pack validator (docs/STATES.md "How to add a state"; test: ruleshapes.test.ts) ─────────────────────────────

/**
 * Every problem with a state's shapes, in plain words (empty = valid): each verified value has a citation, an http(s) URL and
 * a lastChecked date no older than `notBefore` (the latest January 1: the yearly re-check); each unknown says what was tried;
 * each posting is sourced; every quote, and every "…" part of a note, is found by `inSource` (the saved verbatim text).
 */
export function shapeProblems(st: StateShapes, o: { inSource: (quote: string) => boolean; notBefore: IsoDate; quotesRequired?: boolean }): string[] {
  const out: string[] = [];
  const quoted = (note: string) => [...note.matchAll(/"([^"]{12,})"/g)].map((m) => m[1]!.replace(/…/g, "").trim());
  const src = (where: string, s: { citation: string; url: string; quote?: string; lastChecked: string }, note?: string) => {
    if (!s.citation?.trim()) out.push(`${where}: no citation`);
    if (!/^https?:\/\//.test(s.url ?? "")) out.push(`${where}: no source URL`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s.lastChecked ?? "")) out.push(`${where}: no lastChecked date`);
    else if (s.lastChecked < o.notBefore) out.push(`${where}: last checked ${s.lastChecked}, before ${o.notBefore}`);
    if (o.quotesRequired && !s.quote) out.push(`${where}: no quote`);
    if (s.quote && !o.inSource(s.quote)) out.push(`${where}: quote not in the saved source: "${s.quote.slice(0, 60)}"`);
    for (const q of note ? quoted(note) : []) if (!o.inSource(q)) out.push(`${where}: note quote not in the saved source: "${q.slice(0, 60)}"`);
  };
  const layers: [string, AuthorityShapes | RuleShapes][] = [["state", st.state], ...Object.entries(st.authorities).map(([k, v]) => [k, v] as [string, AuthorityShapes])];
  for (const [name, layer] of layers) {
    for (const { path, v } of sourcedValues(layer)) {
      const where = `${name}.${path}`;
      if (v.status === "verified") src(where, v.source, v.note);
      else {
        if (!v.tried?.trim()) out.push(`${where}: unknown without what was tried`);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(v.lastChecked ?? "")) out.push(`${where}: unknown without a lastChecked date`);
      }
    }
    for (const p of layer.postings ?? []) src(`${name}.postings.${p.id}`, p.source);
  }
  return out;
}

/** The latest January 1 on or before `today`: research is renewed each January 1 (the owner's rule). */
export const latestJan1 = (today: IsoDate): IsoDate => `${today.slice(0, 4)}-01-01`;

// ── The binder: the authority's own inspection form, mapped ─────────────────────────────────────────────────────────

/**
 * The binder page "Your health district's inspection form": each item of the authority's own form (SNHD's 32), its weight,
 * and the FDA Form 3-A items (inspectionform.ts) whose records in this binder speak to it. Where no form is sourced: one
 * line, "not mapped yet". `fdaTitle` names an FDA item by number. Only for states whose shapes map a form (Nevada today),
 * so Utah's binder is unchanged.
 */
export function authorityFormSection(r: ResolvedShapes, fdaTitle: (n: number) => string | undefined): import("./binder.ts").BinderSection | null {
  if (!Object.values(SHAPES[r.state].authorities).some((a) => a.scoring && valueOf(a.scoring)?.kind === "demerit_bands")) return null;
  const s = valueOf(r.shapes.scoring);
  const who = r.authority.kind === "confirmed" ? r.authority.department.name : "your health authority";
  if (!s || s.kind !== "demerit_bands") return { id: "authority-form", heading: "Your health district's inspection form", lines: [`${who}: the inspection form is not mapped yet. The FDA model form items are in this binder's index.`] };
  const cite = r.shapes.scoring.status === "verified" ? r.shapes.scoring.source.citation : "";
  const word = { priority: "Priority", priority_foundation: "Priority foundation", core: "Core" } as const;
  return {
    id: "authority-form", heading: "Your health district's inspection form",
    intro: `${who}'s own form, item by item, with the FDA model form items whose records in this binder speak to it. A map of where to look, not a score (${cite}).`,
    table: {
      columns: ["Item", "On their form", "Weight", "FDA model form items"], widths: [0.5, 3.4, 1.2, 2.6],
      rows: s.form.map((it) => ({ cells: [String(it.n), it.title, `${word[it.severity]}${s.weights[it.severity] ? `, ${s.weights[it.severity]} demerits` : ""}`, it.fda.length ? it.fda.map((n) => `${n} ${fdaTitle(n) ?? ""}`.trim()).join("; ") : "Not mapped yet"] })),
    },
  };
}

// ── "Serving customers from today?" (journey.ts GETTING_READY.openWhy), with this place's own card rule ─────────────────

/**
 * What starts the day a place says it's open, from its food handler card shape. Utah's words were shown everywhere ("each food
 * worker's 30-day permit count starts"): wrong in Riverside (7 days), Clark County (before the first shift) and where no card is
 * required (Washoe, Churchill, New York). Utah's sentence is unchanged (its card step: 30 days, "County food handler permit").
 */
export function openWhyFor(place: { state?: string | null; county?: string | null }): string {
  const base = "From today your opening and closing checks run on your hours";
  const fh = valueOf(shapesAt(place).shapes.foodHandler);
  const card = fh?.required ? fh.steps.find((x) => x.id === "card") ?? fh.steps.at(-1) : undefined;
  if (!card) return `${base}.`;
  if (card.due.kind === "within_days") return `${base}, and each food worker's ${card.due.days}-day ${card.label.replace(/\s*\([^)]*\)/g, "").trim().split(/\s+/).at(-1)!.toLowerCase()} count starts.`;
  if (card.due.kind === "before_first_work") return `${base}. Each new food worker needs their ${card.label} before their first shift.`;
  return `${base}.`;
}
